import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
  buildDirectoryTree,
  collectFolderIds,
  type DirectoryTreeNode,
} from "../../utils/buildDirectoryTree";
import { type DirectoryWalkEntry } from "../../utils/recursiveDirectoryWalk";

interface CompareDirectoryTreeProps {
  entries: DirectoryWalkEntry[];
  highlightDiff?: boolean;
}

/** Fixed row height keeps virtualization math exact; names are clipped to one line. */
const ROW_HEIGHT = 36;
const VIEWPORT_MAX_HEIGHT = 360;
const OVERSCAN_ROWS = 8;
/** Above this many entries, start with folders collapsed instead of expanded. */
const AUTO_EXPAND_ENTRY_LIMIT = 300;
// Remove directories that contain no files anywhere in their subtree.
function pruneEmptyFolders(nodes: DirectoryTreeNode[]): DirectoryTreeNode[] {
  const result: DirectoryTreeNode[] = [];
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    if (node.kind === "file") {
      result.push(node);
      continue;
    }
    const prunedChildren = pruneEmptyFolders(node.children);
    if (prunedChildren.length > 0) {
      result.push({ ...node, children: prunedChildren });
    }
  }
  return result;
}

function formatBytes(sizeBytes?: number): string {
  if (sizeBytes == null) {
    return "";
  }
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`;
  }
  if (sizeBytes < 1024 * 1024) {
    return `${(sizeBytes / 1024).toFixed(1)} KB`;
  }
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface FlatRow {
  node: DirectoryTreeNode;
  depth: number;
}

function flattenVisibleRows(
  nodes: DirectoryTreeNode[],
  expandedIds: Set<string>,
  depth: number,
  out: FlatRow[]
): void {
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    out.push({ node, depth });
    if (
      node.kind === "directory" &&
      node.children.length > 0 &&
      expandedIds.has(node.id)
    ) {
      flattenVisibleRows(node.children, expandedIds, depth + 1, out);
    }
  }
}

interface TreeRowProps {
  node: DirectoryTreeNode;
  depth: number;
  isExpanded: boolean;
  onToggleFolder: (folderId: string) => void;
  highlightDiff?: boolean;
}

const TreeRow: React.FC<TreeRowProps> = React.memo(
  ({ node, depth, isExpanded, onToggleFolder, highlightDiff = false }) => {
    const isFolder = node.kind === "directory";
    const hasChildren = node.children.length > 0;
    const childFolderCount = node.children.filter(
      (child) => child.kind === "directory"
    ).length;
    const childFileCount = node.children.length - childFolderCount;
    const isInteractive = isFolder && hasChildren;

    const handleClick = () => {
      if (isInteractive) {
        onToggleFolder(node.id);
      }
    };

    return (
      <Box
        role={isInteractive ? "button" : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        onClick={handleClick}
        onKeyDown={(event) => {
          if (isInteractive && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            onToggleFolder(node.id);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          height: ROW_HEIGHT,
          px: 1,
          pl: 1 + depth * 2,
          cursor: isInteractive ? "pointer" : "default",
          borderRadius: 0.5,
          boxSizing: "border-box",
          "&:hover": isInteractive
            ? { backgroundColor: "rgba(255,255,255,0.06)" }
            : undefined,
          ...(highlightDiff
            ? {
                borderLeft: "3px solid hsl(19 101% 51% / 1)",
                backgroundColor: "rgba(255, 87, 34, 0.08)",
              }
            : undefined),
        }}
        title={node.relativePath}
      >
        <Box sx={{ width: 28, display: "flex", justifyContent: "center", flexShrink: 0 }}>
          {isInteractive ? (
            <IconButton
              size="small"
              aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
              onClick={(event) => {
                event.stopPropagation();
                onToggleFolder(node.id);
              }}
              sx={{ color: "#ccc", p: 0.25 }}
            >
              {isExpanded ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
            </IconButton>
          ) : null}
        </Box>
        {isFolder ? (
          <FolderIcon sx={{ fontSize: 18, color: "hsl(19 101% 51% / 1)", flexShrink: 0 }} />
        ) : (
          <InsertDriveFileIcon sx={{ fontSize: 18, color: "#bbb", flexShrink: 0 }} />
        )}
        <Typography
          variant="body2"
          sx={{
            color: "#eee",
            flex: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: isFolder ? 600 : 400,
          }}
        >
          {node.name}
        </Typography>
        {isFolder && hasChildren && (
          <Typography variant="caption" sx={{ color: "#888", whiteSpace: "nowrap", ml: 1 }}>
            {childFolderCount} folder{childFolderCount === 1 ? "" : "s"}
            {childFileCount > 0
              ? `, ${childFileCount} file${childFileCount === 1 ? "" : "s"}`
              : ""}
          </Typography>
        )}
        {!isFolder && (
          <Typography variant="caption" sx={{ color: "#888", whiteSpace: "nowrap", ml: 1 }}>
            {formatBytes(node.sizeBytes)}
          </Typography>
        )}
      </Box>
    );
  }
);

TreeRow.displayName = "TreeRow";

const CompareDirectoryTree: React.FC<CompareDirectoryTreeProps> = ({
  entries,
  highlightDiff = false,
}) => {
  const fullTree = useMemo(() => buildDirectoryTree(entries), [entries]);
  const tree = useMemo(() => pruneEmptyFolders(fullTree), [fullTree]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(VIEWPORT_MAX_HEIGHT);

  useEffect(() => {
    if (entries.length > AUTO_EXPAND_ENTRY_LIMIT) {
      setExpandedIds(new Set());
      return;
    }
    const rootFolderIds: string[] = [];
    for (let index = 0; index < tree.length; index += 1) {
      if (tree[index].kind === "directory") {
        rootFolderIds.push(tree[index].id);
      }
    }
    setExpandedIds(new Set(rootFolderIds));
  }, [tree, entries.length]);

  const visibleRows = useMemo(() => {
    const rows: FlatRow[] = [];
    flattenVisibleRows(tree, expandedIds, 0, rows);
    return rows;
  }, [tree, expandedIds]);

  // Reset scroll position whenever the visible set changes shape significantly.
  useEffect(() => {
    if (scrollRef.current) {
      const measured = scrollRef.current.clientHeight;
      if (measured > 0) {
        setViewportHeight(measured);
      }
    }
  }, [visibleRows.length]);

  const onToggleFolder = useCallback((folderId: string) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedIds(new Set(collectFolderIds(tree)));
  }, [tree]);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    setScrollTop(target.scrollTop);
    if (target.clientHeight > 0) {
      setViewportHeight(target.clientHeight);
    }
  }, []);

  if (tree.length === 0) {
    return null;
  }

  const totalHeight = visibleRows.length * ROW_HEIGHT;
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS);
  const endIndex = Math.min(
    visibleRows.length,
    Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN_ROWS
  );
  const topSpacer = startIndex * ROW_HEIGHT;
  const bottomSpacer = Math.max(0, totalHeight - endIndex * ROW_HEIGHT);
  const renderedRows = visibleRows.slice(startIndex, endIndex);

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
        <Typography
          component="button"
          type="button"
          variant="caption"
          onClick={expandAll}
          sx={{
            color: "hsl(19 101% 51% / 1)",
            cursor: "pointer",
            border: "none",
            background: "none",
            textDecoration: "underline",
          }}
        >
          Expand all folders
        </Typography>
        <Typography variant="caption" sx={{ color: "#666" }}>
          |
        </Typography>
        <Typography
          component="button"
          type="button"
          variant="caption"
          onClick={collapseAll}
          sx={{
            color: "hsl(19 101% 51% / 1)",
            cursor: "pointer",
            border: "none",
            background: "none",
            textDecoration: "underline",
          }}
        >
          Collapse all
        </Typography>
      </Box>
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          maxHeight: VIEWPORT_MAX_HEIGHT,
          overflowY: "auto",
          border: "1px solid #2a2a2a",
          borderRadius: 1,
          py: 0.5,
        }}
      >
        <Box sx={{ height: topSpacer }} />
        {renderedRows.map((row) => (
          <TreeRow
            key={row.node.id}
            node={row.node}
            depth={row.depth}
            isExpanded={expandedIds.has(row.node.id)}
            onToggleFolder={onToggleFolder}
            highlightDiff={highlightDiff}
          />
        ))}
        <Box sx={{ height: bottomSpacer }} />
      </Box>
    </Box>
  );
};

export default CompareDirectoryTree;
